'use client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography, useTheme as useMuiTheme } from '@mui/material';

import { useT } from '@/hooks';
import { Button } from './Button';

interface LoadMoreButtonProps {
  onClick: () => void;
  isLoading: boolean;
  hasNextPage: boolean;
  label?: string;
  loadingText?: string;
  // Only its length is read (for the empty-state check) — accepts any list type.
  events: unknown[];
}

/**
 * Enhanced LoadMoreButton component with premium aesthetics and animations.
 */
export const LoadMoreButton = ({
  onClick,
  isLoading,
  hasNextPage,
  label = 'Load More',
  loadingText = 'Loading...',
  events,
}: LoadMoreButtonProps) => {
  const theme = useMuiTheme();
  const { t } = useT();

  const finalLabel = label === 'Load More' ? t('portfolio.loadMore') : label;
  const finalLoadingText = loadingText === 'Loading...' ? t('common.loading') : loadingText;

  if (events.length === 0 && !hasNextPage && !isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            opacity: 0.5,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            fontWeight: 600,
            mt: 4,
            mb: 6,
          }}
        >
          {t('common.noData')}
        </Typography>
      </Box>
    );
  }

  return hasNextPage ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: 4,
        mb: 6,
        width: '100%',
      }}
    >
      <Button
        variant="text"
        color="primary"
        onClick={onClick}
        isLoading={isLoading}
        loadingText={finalLoadingText}
        endIcon={!isLoading && <ExpandMoreIcon sx={{ fontSize: '1.2rem !important' }} />}
        sx={{
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          py: 1,
          px: 2,
          minWidth: 'auto',
          borderRadius: 1,
          color: 'text.secondary',
          '&:hover': {
            bgcolor: 'action.hover',
            color: 'primary.main',
            '& .MuiButton-endIcon': {
              transform: 'translateY(2px)',
            },
          },
          transition: 'all 0.2s ease',
          '& .MuiButton-endIcon': {
            transition: 'transform 0.2s ease',
            ml: 0.5,
          },
        }}
      >
        {finalLabel}
      </Button>
    </Box>
  ) : null;
};
